import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_RECURRING_JOBS, CANCEL_RECURRING_JOB_AND_RELATED_JOBS, UPDATE_RECURRING_JOB, GET_SERVICE_TYPES, GET_USERS, DELETE_RECURRING_JOB } from '../graphql/queries';
import { toast } from 'react-hot-toast';

const RecurringJobs = () => {
  const [selectedRecurringJob, setSelectedRecurringJob] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  // Fetch all recurring jobs with computed job counts
  const { 
    data: recurringJobsData, 
    loading: recurringJobsLoading, 
    refetch: refetchRecurringJobs,
    error: recurringJobsError
  } = useQuery(GET_RECURRING_JOBS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      console.log('Recurring jobs loaded:', data?.recurringJobs?.length);
      data?.recurringJobs?.forEach(job => {
        console.log(`${job.name}: ${job.totalJobs} total, ${job.pendingJobs} pending, ${job.completedJobs} completed`);
      });
    },
    onError: (error) => {
      console.error('Error fetching recurring jobs:', error);
      toast.error('Error loading recurring jobs');
    }
  });

  // Fetch service types for edit form
  const { data: serviceTypesData } = useQuery(GET_SERVICE_TYPES, {
    fetchPolicy: 'cache-and-network'
  });

  // Fetch users for assignment dropdown
  const { data: usersData } = useQuery(GET_USERS, {
    fetchPolicy: 'cache-and-network'
  });

  // Mutations
  const [updateRecurringJob] = useMutation(UPDATE_RECURRING_JOB, {
    onCompleted: () => {
      toast.success('Recurring job updated successfully');
      refetchRecurringJobs();
      setShowEditModal(false);
      setSelectedRecurringJob(null);
      setEditFormData({});
      setIsProcessing(false);
    },
    onError: (error) => {
      toast.error(`Error updating recurring job: ${error.message}`);
      setIsProcessing(false);
    }
  });

  const [deleteRecurringJob] = useMutation(DELETE_RECURRING_JOB, {
    onCompleted: () => {
      toast.success('Recurring job template deleted successfully');
      refetchRecurringJobs();
      setIsProcessing(false);
    },
    onError: (error) => {
      toast.error(`Error deleting recurring job: ${error.message}`);
      setIsProcessing(false);
    }
  });

  const [cancelRecurringJobAndRelatedJobs] = useMutation(CANCEL_RECURRING_JOB_AND_RELATED_JOBS, {
    onCompleted: (data) => {
      const canceledJobsCount = data.cancelRecurringJobAndRelatedJobs;
      toast.success(`Recurring job canceled successfully. ${canceledJobsCount} related jobs were also canceled.`);
      refetchRecurringJobs();
      setShowCancelModal(false);
      setSelectedRecurringJob(null);
      setIsProcessing(false);
    },
    onError: (error) => {
      toast.error(`Error canceling recurring job: ${error.message}`);
      setIsProcessing(false);
    }
  });

  // Handle canceling a recurring job and all its related jobs
  const handleCancelRecurringJob = async (recurringJob) => {
    setIsProcessing(true);
    
    try {
      await cancelRecurringJobAndRelatedJobs({
        variables: { id: recurringJob.id }
      });
    } catch (error) {
      console.error('Error in cancel process:', error);
      toast.error('Error canceling recurring job');
      setIsProcessing(false);
    }
  };

  // Handle opening edit modal
  const handleEditRecurringJob = (recurringJob) => {
    setSelectedRecurringJob(recurringJob);
    setEditFormData({
      id: recurringJob.id,
      name: recurringJob.name,
      category: recurringJob.category,
      frequency: recurringJob.frequency,
      startTime: recurringJob.startTime,
      notes: recurringJob.notes || '',
      serviceTypeId: recurringJob.serviceTypeId || '',
      assignedToId: recurringJob.assignedToId || '',
      dayOfWeek: recurringJob.dayOfWeek || '',
      weekOfMonth: recurringJob.weekOfMonth || '',
      dayOfMonth: recurringJob.dayOfMonth || ''
    });
    setShowEditModal(true);
  };

  // Handle form input changes
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Prepare the input data, removing empty values
      const input = {
        id: editFormData.id,
        name: editFormData.name,
        category: editFormData.category,
        frequency: editFormData.frequency,
        startTime: editFormData.startTime,
        notes: editFormData.notes || null,
        serviceTypeId: editFormData.serviceTypeId || null,
        assignedToId: editFormData.assignedToId ? parseInt(editFormData.assignedToId) : null,
        dayOfWeek: editFormData.dayOfWeek ? parseInt(editFormData.dayOfWeek) : null,
        weekOfMonth: editFormData.weekOfMonth ? parseInt(editFormData.weekOfMonth) : null,
        dayOfMonth: editFormData.dayOfMonth ? parseInt(editFormData.dayOfMonth) : null
      };

      await updateRecurringJob({
        variables: { input }
      });
    } catch (error) {
      console.error('Error updating recurring job:', error);
      toast.error('Error updating recurring job');
      setIsProcessing(false);
    }
  };

  // Handle hard delete (for duplicates/cleanup)
  const handleHardDelete = async (recurringJob) => {
    setIsProcessing(true);
    
    try {
      await deleteRecurringJob({
        variables: { id: recurringJob.id }
      });
    } catch (error) {
      console.error('Error in hard delete process:', error);
      toast.error('Error deleting recurring job');
      setIsProcessing(false);
    }
  };

  // Format frequency display
  const formatFrequency = (frequency) => {
    switch (frequency) {
      case 'DAILY': return 'Daily';
      case 'WEEKLY': return 'Weekly';
      case 'BIWEEKLY': return 'Bi-weekly';
      case 'MONTHLY': return 'Monthly';
      case 'QUARTERLY': return 'Quarterly';
      default: return frequency;
    }
  };

  // Format category display
  const formatCategory = (category) => {
    switch (category) {
      case 'CLEANING': return 'Cleaning';
      case 'INSPECTION': return 'Inspection';
      case 'MAINTENANCE': return 'Maintenance';
      case 'WALKTHROUGH': return 'Walkthrough';
      default: return category;
    }
  };

  if (recurringJobsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <div className="ml-4">
          <p className="text-gray-600">Loading recurring job templates...</p>
        </div>
      </div>
    );
  }

  if (recurringJobsError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-lg font-medium text-red-800">Error Loading Recurring Jobs</h3>
          <p className="text-red-600 mt-2">{recurringJobsError.message}</p>
        </div>
      </div>
    );
  }

  const recurringJobs = recurringJobsData?.recurringJobs || [];
  const totalJobsCreated = recurringJobs.reduce((sum, job) => sum + (job.totalJobs || 0), 0);
  const totalPendingJobs = recurringJobs.reduce((sum, job) => sum + (job.pendingJobs || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Recurring Job Templates</h1>
        <p className="mt-2 text-gray-600">
          Manage your recurring job templates and view their associated jobs
        </p>
      </div>

      {/* Warning Notice */}
      <div className="mb-8 bg-amber-50 border border-amber-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">Important Notice</h3>
            <div className="mt-2 text-sm text-amber-700">
              <p>
                When you cancel and archive a recurring job template, the system will:
              </p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Cancel all pending and in-progress jobs</li>
                <li>Keep completed jobs for record-keeping</li>
                <li>Permanently delete the template (this cannot be undone)</li>
                <li>Stop generating new jobs from this template</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Templates</p>
              <p className="text-2xl font-semibold text-gray-900">{recurringJobs.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Jobs Created</p>
              <p className="text-2xl font-semibold text-gray-900">{totalJobsCreated}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Pending Jobs</p>
              <p className="text-2xl font-semibold text-gray-900">{totalPendingJobs}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recurring Jobs Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Recurring Job Templates</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Jobs Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recurringJobs.map((recurringJob) => {
                return (
                  <tr key={recurringJob.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {recurringJob.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatCategory(recurringJob.category)}
                          {recurringJob.serviceType && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {recurringJob.serviceType.title}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{recurringJob.property.name}</div>
                      <div className="text-sm text-gray-500">{recurringJob.property.propertyCode}</div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatFrequency(recurringJob.frequency)}</div>
                      <div className="text-sm text-gray-500">
                        Start: {recurringJob.startTime}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <span className="font-medium">{recurringJob.totalJobs || 0}</span> total
                      </div>
                      <div className="text-xs text-gray-500 space-x-2">
                        <span className="text-yellow-600">{recurringJob.pendingJobs || 0} pending</span>
                        <span className="text-green-600">{recurringJob.completedJobs || 0} completed</span>
                        {(recurringJob.canceledJobs || 0) > 0 && (
                          <span className="text-red-600">{recurringJob.canceledJobs} canceled</span>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEditRecurringJob(recurringJob)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                      >
                        Edit
                      </button>
                      
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to permanently delete "${recurringJob.name}"? This action cannot be undone.`)) {
                            handleHardDelete(recurringJob);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200"
                        title="Hard delete (for duplicates/cleanup)"
                      >
                        Hard Delete
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedRecurringJob(recurringJob);
                          setShowCancelModal(true);
                        }}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                      >
                        Cancel & Archive
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {recurringJobs.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No recurring jobs</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a recurring job template.
            </p>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedRecurringJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 text-center mt-4">
                Cancel Recurring Job Template
              </h3>
              
              <div className="mt-4 text-sm text-gray-500">
                <p className="mb-3">
                  This will permanently cancel the recurring job template "{selectedRecurringJob.name}" and:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li>Cancel all {selectedRecurringJob.pendingJobs || 0} pending jobs</li>
                  <li>Archive the recurring job template</li>
                  <li>This action cannot be undone</li>
                </ul>
                <p className="text-red-600 font-medium">
                  Are you sure you want to proceed?
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedRecurringJob(null);
                  }}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleCancelRecurringJob(selectedRecurringJob)}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                >
                  {isProcessing && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isProcessing ? 'Processing...' : 'Yes, Cancel & Archive'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRecurringJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border max-w-2xl shadow-lg rounded-md bg-white">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Edit Recurring Job Template: {selectedRecurringJob.name}
              </h3>
              
              <form onSubmit={handleEditFormSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.name || ''}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={editFormData.category || ''}
                    onChange={(e) => handleEditFormChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="CLEANING">Cleaning</option>
                    <option value="INSPECTION">Inspection</option>
                    <option value="MAINTENANCE">Maintenance</option>
                    <option value="WALKTHROUGH">Walkthrough</option>
                  </select>
                </div>

                {/* Service Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type (Optional)
                  </label>
                  <select
                    value={editFormData.serviceTypeId || ''}
                    onChange={(e) => handleEditFormChange('serviceTypeId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No specific service type</option>
                    {serviceTypesData?.serviceTypes?.filter(type => type.isActive).map(serviceType => (
                      <option key={serviceType.id} value={serviceType.id}>
                        {serviceType.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency
                  </label>
                  <select
                    value={editFormData.frequency || ''}
                    onChange={(e) => handleEditFormChange('frequency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="BIWEEKLY">Bi-weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                  </select>
                </div>

                {/* Frequency-specific fields */}
                {(editFormData.frequency === 'WEEKLY' || editFormData.frequency === 'BIWEEKLY') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day of Week
                    </label>
                    <select
                      value={editFormData.dayOfWeek || ''}
                      onChange={(e) => handleEditFormChange('dayOfWeek', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select day</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                      <option value="0">Sunday</option>
                    </select>
                  </div>
                )}

                {editFormData.frequency === 'MONTHLY' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Week of Month (Optional)
                      </label>
                      <select
                        value={editFormData.weekOfMonth || ''}
                        onChange={(e) => handleEditFormChange('weekOfMonth', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select week</option>
                        <option value="1">First week</option>
                        <option value="2">Second week</option>
                        <option value="3">Third week</option>
                        <option value="4">Fourth week</option>
                        <option value="5">Last week</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day of Month (Optional)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={editFormData.dayOfMonth || ''}
                        onChange={(e) => handleEditFormChange('dayOfMonth', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Day of month (1-31)"
                      />
                    </div>
                  </>
                )}

                {(editFormData.frequency === 'QUARTERLY') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day of Month
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={editFormData.dayOfMonth || ''}
                      onChange={(e) => handleEditFormChange('dayOfMonth', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Day of month (1-31)"
                    />
                  </div>
                )}

                {/* Start Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={editFormData.startTime || ''}
                    onChange={(e) => handleEditFormChange('startTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Assigned To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned To (Optional)
                  </label>
                  <select
                    value={editFormData.assignedToId || ''}
                    onChange={(e) => handleEditFormChange('assignedToId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No assignment</option>
                    {usersData?.users?.filter(user => user.isActive).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={editFormData.notes || ''}
                    onChange={(e) => handleEditFormChange('notes', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Additional notes or instructions..."
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedRecurringJob(null);
                      setEditFormData({});
                    }}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {isProcessing && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isProcessing ? 'Updating...' : 'Update Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurringJobs; 